import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/system';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import HelpIcon from '@mui/icons-material/Help';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import { useTranslation } from '@hooks';
import {
    formatNumber,
    formatNumberParts,
    formatOptionalNumber,
    formatPercentNumber,
    formatPercentNumberParts,
    isMissing,
} from '@helpers/formatNumbers';

const ValueRow = styled('div')({
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: '4px',
});

const ValueSuffix = styled('span')({
    fontFamily: 'Rubik, sans-serif',
    fontSize: '1.5rem',
    fontWeight: 400,
    lineHeight: '32px',
    color: 'rgba(0, 0, 0, 0.38)',
});

const arrowSx = { marginRight: '11px', fontSize: '20px' };

const roundAwayFromZero = value => Math.sign(value) * Math.round(Math.abs(value));

const getDeltaResult = (field, withInvertion) => {
    if (field === 0 || Number.isNaN(field)) {
        return { color: 'var(--success-main)', icon: null };
    }
    const isImprovement = withInvertion ? field < 0 : field > 0;
    return {
        color: isImprovement ? 'var(--success-main)' : 'var(--error-main)',
        icon: field > 0 ? <ArrowUpwardIcon sx={arrowSx} /> : <ArrowDownwardIcon sx={arrowSx} />,
    };
};

const MetricItem = ({
    data,
    title,
    tooltip,
    styles,
    withPercentage,
    withPercentageDelta,
    withInvertion,
    value,
    notation = 'compact',
    deltaLabel,
    formatDelta,
}) => {
    const { t } = useTranslation();
    const rawDelta = parseFloat(data?.delta);
    const delta = withPercentageDelta ? roundAwayFromZero(rawDelta) : rawDelta;
    const hasDelta = !Number.isNaN(delta) && delta !== 0;
    const { color, icon } = getDeltaResult(delta, withInvertion);
    const metric = data?.metric;
    const { prefix, number, suffix } =
        value ??
        (isMissing(metric)
            ? { prefix: '', number: formatOptionalNumber(metric), suffix: '' }
            : withPercentage
              ? formatPercentNumberParts(metric)
              : formatNumberParts(metric, notation));
    const deltaText = !hasDelta
        ? ''
        : formatDelta
          ? formatDelta(delta)
          : withPercentageDelta
            ? formatPercentNumber(delta)
            : formatNumber(data.delta);
    return (
        <Paper
            elevation={1}
            sx={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                borderRadius: '8px',
                ...styles,
            }}
        >
            <Typography variant="title1" color="textPrimary" align="center" sx={{ alignSelf: 'center' }}>
                {tooltip ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        {title}
                        <Tooltip title={tooltip} placement="top" enterTouchDelay={0}>
                            <IconButton sx={{ padding: '4px !important', marginLeft: '4px' }}>
                                <HelpIcon sx={{ height: '18px !important', width: '18px !important' }} />
                            </IconButton>
                        </Tooltip>
                    </span>
                ) : (
                    title
                )}
            </Typography>
            <ValueRow>
                <Typography variant="h3" component="span" sx={{ color: 'var(--primary-main)' }}>
                    {prefix}
                    {number}
                </Typography>
                {suffix && <ValueSuffix>{suffix}</ValueSuffix>}
            </ValueRow>
            <Typography
                variant="caption"
                sx={{ alignSelf: 'center', color, display: 'flex', alignItems: 'flex-end', height: '28px' }}
            >
                {hasDelta && icon}
                {hasDelta &&
                    `${delta > 0 ? '+' : ''}${deltaText} ${deltaLabel ?? t('AnalyticsConversion.fromPreviousPeriod')}`}
            </Typography>
        </Paper>
    );
};

export default MetricItem;
